/**
 * EditCareInstructionsForm (#614 follow-up) — the Pet Profile's second write
 * path. PATCHes /api/admin/dogs/:dogId/care-instructions (tenant-scoped
 * server-side) and asks the page to refetch on success, so the rendered value
 * always comes from the server rather than optimistic local state.
 *
 * Why staff need this at all: the platform could only ever RECEIVE care
 * instructions from the owner's app. Anything the front desk was told in
 * person ("he's on ear drops this week") had nowhere to go.
 *
 * Five states, per the Design Philosophy: default · submitting (disabled) ·
 * error (says what happened, PRESERVES the text so a retry costs nothing) ·
 * empty (placeholder invites the first entry) · success (closes).
 *
 * Clearing the box saves null — "no instructions on file" must stay
 * distinguishable from "instructions that are an empty string".
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  humanizeApiError,
  readSession,
  updateDogCareInstructions,
} from '../../lib/api.js';

interface EditCareInstructionsFormProps {
  apiUrl: string;
  dogId: string;
  /** Current stored value; null when nothing is on file. */
  value: string | null;
  /** Called after a successful save so the page reloads the profile. */
  onSaved: () => void;
  onCancel: () => void;
}

const MAX_LENGTH = 2000;

export function EditCareInstructionsForm({
  apiUrl,
  dogId,
  value,
  onSaved,
  onCancel,
}: EditCareInstructionsFormProps): JSX.Element {
  const [text, setText] = useState(value ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textRef.current?.focus();
  }, []);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const { token } = readSession();
    if (!token) {
      setError('Your session has expired — sign in again to save.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Empty box clears the field. The server normalises '' → null too;
      // doing it here as well keeps the request honest about intent.
      const trimmed = text.trim();
      await updateDogCareInstructions(
        apiUrl,
        token,
        dogId,
        trimmed.length === 0 ? null : trimmed,
      );
      onSaved();
    } catch (err) {
      const raw =
        err instanceof Error ? err.message : 'Failed to save care instructions';
      setError(humanizeApiError(raw));
      setSubmitting(false);
    }
  };

  return (
    <form
      className="ferro-dogprofile__note-form"
      onSubmit={onSubmit}
      data-testid="dogprofile-care-form"
    >
      <label
        className="ferro-dogprofile__field-label"
        htmlFor="dogprofile-care-text"
      >
        Care instructions
      </label>
      <textarea
        id="dogprofile-care-text"
        ref={textRef}
        className="ferro-dogprofile__textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        maxLength={MAX_LENGTH}
        placeholder="How should staff care for this dog? (e.g. Two scoops at 7am. Ear drops after swimming.)"
        disabled={submitting}
      />
      <p className="ferro-dogprofile__field-hint">
        Visible to staff and to the owner. Leave empty to remove.
      </p>
      {error && (
        <p className="ferro-dogprofile__form-error" role="alert">
          {error}
        </p>
      )}
      <div className="ferro-dogprofile__note-actions">
        <button
          type="submit"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--primary"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
